import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Team {
  id: number;
  name: string;
  abbreviation?: string;
  logo?: string;
  color_primary?: string;
  color_secondary?: string;
  color_text?: string;
  prefix?: string;
}

export interface Division {
  id: number;
  name: string;
  teams: Team[];
}

export interface Conference {
  id: number;
  name: string;
  divisions: Division[];
}

export interface TeamRecord {
  created_at: string;
  games_behind: string;
  id: string;
  league_id: string;
  losses: number;
  season: number;
  team_id: string;
  updated_at: string;
  wins: number;
}

export interface MyLeague {

  id: number;
  name: string;
  logo?: string;
  year?: number;
  embargoed?: 0 | 1 | 2;
  conferences: Conference[];
  dh?: string;
  roster_editing?: string;
  /** When 'locked' or 'frozen' (any case), lineup/rotation edits should be blocked in UI and mutations. */
  lineup_editing?: string;
  /** Some payloads use this name instead of lineup_editing; same semantics. */
  league_editing?: string;
  teamRecords: TeamRecord[];
}

const LOCKED_LINEUP_EDITING_VALUES = new Set(['locked', 'frozen']);

/** True if league disallows lineup/rotation edits (case-insensitive locked | frozen). */
export function isLineupEditingDisabled(
  league: { lineup_editing?: string; league_editing?: string } | null | undefined
): boolean {
  const raw = league?.lineup_editing ?? league?.league_editing;
  const v = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  return LOCKED_LINEUP_EDITING_VALUES.has(v);
}

interface MyLeagueState {
  myLeague: MyLeague | null;
  setMyLeague: (league: MyLeague | null) => void;
  clearMyLeague: () => void;
  setMyTeam: (myTeamId: number) => void;
}

export const useMyLeagueStore = create<MyLeagueState>()(
  persist(
    (set) => ({
      myLeague: null,
      setMyLeague: (myLeague) => set({ myLeague }),
      clearMyLeague: () => set({ myLeague: null }),
      setMyTeam: (myTeamId: number) => {
        // Update local state only 
        const t = myTeamId;
        set((state: MyLeagueState) => ({
          myLeague: state.myLeague ? { ...state.myLeague, myTeamId } : null
        }));
      }
    }),
    { name: 'myLeague' }
  )
); 